import { read, utils } from 'xlsx';

export const parseCsv = ( csv, delimiter ) => {
    delimiter = ( delimiter || ',' ); // user-supplied delimeter or default comma

    const pattern = new RegExp( // regular expression to parse the CSV values.
        ( // Delimiters:
        '(\\' + delimiter + '|\\r?\\n|\\r|^)' +
        // Quoted fields.
        '(?:"([^"]*(?:""[^"]*)*)"|' +
        // Standard fields.
        '([^"\\' + delimiter + '\\r\\n]*))'
        ), 'gi'
    );

    const rows = [ [] ];  // array to hold our data. First row is column headers.
    // array to hold our individual pattern matching groups:
    let matches = false; // false if we don't find any matches
    // Loop until we no longer find a regular expression match
    while ( matches = pattern.exec( csv ) ) { // eslint-disable-line no-cond-assign
        const matched_delimiter = matches[ 1 ]; // Get the matched delimiter
        // Check if the delimiter has a length (and is not the start of string)
        // and if it matches field delimiter. If not, it is a row delimiter.
        if ( matched_delimiter.length && matched_delimiter !== delimiter ) {
            // Since this is a new row of data, add an empty row to the array.
            rows.push( [] );
        }
        let matched_value;
        // Once we have eliminated the delimiter, check to see
        // what kind of value was captured (quoted or unquoted):
        if ( matches[ 2 ] ) { // found quoted value. unescape any double quotes.
            matched_value = matches[ 2 ].replace(
            new RegExp( '""', 'g' ), '"'
            );
        } else { // found a non-quoted value
            matched_value = matches[ 3 ];
        }
        // Now that we have our value string, let's add
        // it to the data array.
        rows[ rows.length - 1 ].push( matched_value );
    }

    // Turn the rows of value arrays into json records
    const records = [];
    const headerLine = rows[ 0 ];
    const startIndex = 1;
    rows.forEach( ( row, rowIndex ) => {
        if ( rowIndex >= startIndex ) {
            const record = {};
            headerLine.forEach( ( header, headerIndex ) => {
                if ( row[ headerIndex ] && header ) {
                    record[ header ] = row[ headerIndex ];
                }
            } );
            records.push( record );
        }
    } );

    return records;
};
export const createCsv = ( records, additionalColumns, getColumnOrder ) => {
    const lines = [];

    if ( records.length > 0 ) {
        let headers = [];
        const headersMap = {};
        records.forEach( record => {
            Object.keys( record ).forEach( header => {
                if ( !headersMap[ header.toLowerCase() ] ) {
                    headersMap[ header.toLowerCase() ] = true;
                    headers.push( header );
                }
            } );
        } );
        if ( Array.isArray( additionalColumns ) ) {
            additionalColumns.forEach( column => {
                const alias = column.title || column.alias;
                if ( !headersMap[ column.dataIndex.toLowerCase() ] && ( !alias || !headersMap[ alias.toLowerCase() ] ) ) {
                    headersMap[ column.dataIndex.toLowerCase() ] = true;
                    headers.push( column.dataIndex );
                }
            } );
        }

        if ( typeof getColumnOrder === 'function' ) {
            headers = headers.sort( ( a, b ) => {
                return getColumnOrder( a ) - getColumnOrder( b );
            } );
        }

        const headerLine = [];
        headers.forEach( ( header ) => {
            headerLine.push( `"${ header }"` );
        } );
        lines.push( headerLine.join( ',' ) );

        records.forEach( ( record ) => {
            const line = [];
            headers.forEach( ( header ) => {
                line.push( `"${ ( record[ header ] || '' ).replace( /"/g, '""' ) }"` );
            } );
            lines.push( line.join( ',' ) );
        } );
    }

    return lines.join( '\r\n' );
};

export const parseFile = ( file ) => {
    return new Promise( ( resolve, reject ) => {
        let reader = new FileReader();
        const rABS = !!reader.readAsBinaryString;

        const fileName = file.name;
        const fileExtension = `.${ fileName.toLowerCase().split( '.' ).slice( -1 )[ 0 ] }`;

        const isCsv = [ '.csv' ].includes( fileExtension );
        const isExcel = [ '.xls', '.xlsx' ].includes( fileExtension );
        const isJson = [ '.json' ].includes( fileExtension );

        if ( !isCsv && !isExcel && !isJson ) {
            reject( new Error( 'Unsupported File Type' ) );
        }

        reader.onload = ( event ) => {
            reader = null;

            if ( isJson ) {
                return resolve( JSON.parse( event.target.result ) );
            } else if ( isCsv ) {
                return resolve( parseCsv( event.target.result, ',' ) );
            } else if ( isExcel ) {
                const bstr = event.target.result;
                const wb = read( bstr, { type: rABS ? 'binary' : 'array' } );
                const wsname = wb.SheetNames[ 0 ];
                const ws = wb.Sheets[ wsname ];

                return resolve( parseCsv( utils.sheet_to_csv( ws ).trim(), ',' ) );
            } else {
                reject( new Error( 'Unsupported File Type' ) );
            }
        };

        if ( isExcel ) {
            if ( rABS ) {
                reader.readAsBinaryString( file );
            } else {
                reader.readAsArrayBuffer( file );
            }
        } else {
            reader.readAsText( file );
        }
    } );
};
export const createFile = ( records, fileName )=> {
    const content = createCsv( records );
    return new File( [ content ], `${ fileName }.csv`, { type: 'text/csv' } );
};